import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ToastProvider";
import { post } from "@/lib/api";
import { formatRelativeTime } from "@/lib/time";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

export function EngagementHub() {
  const { data: recent, isLoading: loadingRecent, mutate: mutateRecent } = useSWR("/marketing/community/recent", fetcher);
  const { data: topFans, isLoading: loadingFans, mutate: mutateFans } = useSWR("/marketing/community/top-fans", fetcher);
  const { push } = useToast();
  const [selectedFan, setSelectedFan] = useState<string>("");
  const [badge, setBadge] = useState("Superfan");
  const [note, setNote] = useState("Thank you for supporting the tour!");

  const handleAward = async () => {
    if (!selectedFan) {
      push({ title: "Select fan", message: "Choose a supporter before awarding.", variant: "destructive" });
      return;
    }
    try {
      await post("/marketing/community/award-badge", { fan_id: selectedFan, badge });
      push({ title: "Badge awarded", message: `Sent ${badge} badge to ${selectedFan}.` });
      await mutateFans();
    } catch (error) {
      push({
        title: "Unable to award",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const fanList = topFans?.fans ?? [];
  const interactions = recent?.items ?? [];

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">Engagement Hub</p>
          <p className="text-xs text-muted-foreground">Top superfans and pending replies.</p>
        </div>
        <Button size="sm" onClick={() => void mutateRecent()}>Refresh</Button>
      </div>

      <section>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Recent interactions</p>
        {loadingRecent ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-2 space-y-2">
            {interactions.map((item: any) => (
              <div key={item.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>@{item.username} on {item.platform}</span>
                  <span>{formatRelativeTime(item.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-foreground">{item.message}</p>
                {item.requires_reply ? (
                  <Badge variant="outline" className="mt-2 border-warning/40 text-warning">Needs reply</Badge>
                ) : null}
              </div>
            ))}
            {!interactions.length ? <p className="text-xs text-muted-foreground">No recent items.</p> : null}
          </div>
        )}
      </section>

      <section>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Top fans</p>
        {loadingFans ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-2 space-y-2">
            {fanList.map((fan: any) => (
              <label
                key={fan.id}
                className="flex cursor-pointer items-center justify-between rounded-xl border border-border/70 bg-background/40 p-3 text-sm text-foreground/80"
              >
                <div>
                  <p className="font-semibold text-foreground">{fan.name}</p>
                  <p className="text-xs text-muted-foreground">{fan.city} · {fan.interactions} interactions</p>
                  <p className="text-[11px] text-muted-foreground">Badges: {(fan.badges || []).join(", ") || "None"}</p>
                </div>
                <input
                  type="radio"
                  className="h-4 w-4"
                  checked={selectedFan === fan.id}
                  onChange={() => setSelectedFan(fan.id)}
                />
              </label>
            ))}
            {!fanList.length ? <p className="text-xs text-muted-foreground">No fans yet.</p> : null}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Award badge</p>
        <Input value={badge} onChange={(event) => setBadge(event.target.value)} className="bg-background/40 text-foreground" />
        <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} className="bg-background/40 text-foreground" />
        <Button onClick={() => void handleAward()} disabled={!selectedFan}>
          Send badge
        </Button>
      </section>
    </Card>
  );
}
